import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { FaArrowRight, FaSearch } from 'react-icons/fa';
import { useSiteSearch } from '../../hooks/useSiteSearch';
import {
  buildSearchResultsNavigation,
  getSearchResultMeta,
  searchSiteDocuments,
} from '../../utils/siteSearch';
import './SearchResults.css';

const SearchResults = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const query = String(searchParams.get('q') || '').trim();
  const [searchInput, setSearchInput] = useState(query);
  const { searchDocuments, isSearchIndexLoading } = useSiteSearch();

  useEffect(() => {
    setSearchInput(query);
  }, [query]);

  const results = query
    ? searchSiteDocuments(query, searchDocuments, { limit: 60 })
    : [];

  const handleSubmit = (event) => {
    event.preventDefault();

    const trimmedSearch = searchInput.trim();

    if (!trimmedSearch) {
      navigate('/search');
      return;
    }

    navigate(buildSearchResultsNavigation(trimmedSearch));
  };

  return (
    <section className="search-results-page">
      <div className="search-results-shell">
        <div className="search-results-header">
          <p className="search-results-eyebrow">Full-Site Search</p>
          <h1 className="search-results-title">Search Across the Entire Website</h1>
          <p className="search-results-intro">
            Find matching pages, products, news posts, catalogues, certificates, and contact content from one place.
          </p>
        </div>

        <form className="search-results-form" onSubmit={handleSubmit}>
          <div className="search-results-input-wrap">
            <FaSearch className="search-results-input-icon" />
            <input
              type="text"
              value={searchInput}
              onChange={(event) => setSearchInput(event.target.value)}
              placeholder="Search any word or phrase..."
              aria-label="Search website content"
            />
          </div>
          <button type="submit" className="search-results-submit">
            Search
          </button>
        </form>

        {!query ? (
          <div className="search-results-state">
            <h2>Start with a keyword or phrase</h2>
            <p>
              Try product names, features, industries, catalogue titles, certificate names, addresses, or news topics.
            </p>
          </div>
        ) : isSearchIndexLoading && results.length === 0 ? (
          <div className="search-results-state">
            <h2>Building search results...</h2>
            <p>We are scanning products, pages, news, catalogues, and certificates for "{query}".</p>
          </div>
        ) : results.length === 0 ? (
          <div className="search-results-state">
            <h2>No matches found for "{query}"</h2>
            <p>Try a broader term, a shorter phrase, or a different spelling.</p>
          </div>
        ) : (
          <>
            <div className="search-results-summary">
              <h2>
                {results.length} result{results.length === 1 ? '' : 's'} for "{query}"
              </h2>
              {isSearchIndexLoading && (
                <p>Refreshing the site index in the background for the latest content.</p>
              )}
            </div>

            <div className="search-results-list">
              {results.map((result) => (
                <Link key={result.id} to={result.navigation} className="search-result-card">
                  <div className="search-result-copy">
                    <span className="search-result-meta">{getSearchResultMeta(result)}</span>
                    <h3>{result.title}</h3>
                    {result.snippet && <p>{result.snippet}</p>}
                  </div>
                  <span className="search-result-action" aria-hidden="true">
                    <FaArrowRight />
                  </span>
                </Link>
              ))}
            </div>
          </>
        )}
      </div>
    </section>
  );
};

export default SearchResults;
